// Where a signed-out visitor was going when the auth guard sent them to Login, so the login that follows lands there
// instead of Home (a mailed report link, a bookmarked board). `AuthorizationPage` remembers it; the password/OTP,
// Google and SSO completions take it once. Same tab only: sessionStorage survives the SSO full-page redirect, never
// another tab.
//
// ⚠️ An open-redirect surface: whatever is stored is navigated to after a real login. Only same-origin relative paths
// pass, checked on write AND on read, and never an auth page (a loop, or a token in a url). The check is Miro's
// `isSafeReturnPath` rule, kept here so the auth guard does not import the Miro module (and with it the API client).

const STORAGE_KEY = "post_login_redirect";

/** A destination older than this is abandoned: a later, unrelated login must not be sent there. */
export const POST_LOGIN_REDIRECT_TTL_MS = 30 * 60 * 1000;
const AUTH_PAGES = ["/login", "/signup", "/auth", "/set-password", "/reset-password"];

/**
 * Judged on the pathname the ROUTER will see: dot segments resolved and percent-escapes decoded (`/%6cogin` and
 * `/x/../login` both match the Login route). A pathname that does not decode is refused outright.
 */
const isAuthPage = (path: string) => {
  let pathname: string;
  try {
    pathname = decodeURIComponent(new URL(path, "http://local").pathname).toLowerCase();
  } catch {
    return true;
  }

  return AUTH_PAGES.some((page) => pathname === page || pathname.startsWith(`${page}/`));
};

/** `/x` only: not `//host`, not a backslash variant some browsers turn into `/`, no whitespace or control characters. */
const isSafeRelativePath = (value: unknown): value is string =>
  // eslint-disable-next-line no-control-regex
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//") && !value.includes("\\") && !/[\u0000-\u001f\u007f\s]/.test(value);

const isUsable = (path: unknown): path is string => isSafeRelativePath(path) && path !== "/" && !isAuthPage(path);

/**
 * Remember a protected url (path + query + hash). The latest protected visit wins: one that cannot be remembered
 * (Home, an unsafe url) clears the older one, so abandoning a report for Home does not send the login to the report.
 * An auth page is the exception — it is ignored, never a reason to clear: no protected route lives there, so reading
 * one means the guard's redirect to Login has already happened (render navigates, then the effect reads the url).
 */
export const rememberPostLoginPath = (path: string) => {
  if (!isUsable(path)) {
    if (!(isSafeRelativePath(path) && isAuthPage(path))) clearPostLoginPath();

    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Storage blocked (private mode, quota): the login still works and lands Home, as before.
  }
};

/** True when nothing is stored any more; false when storage refused the removal. */
export const clearPostLoginPath = (): boolean => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);

    return true;
  } catch {
    return false;
  }
};

/** The remembered url, cleared as it is read — null when there is none, it expired, or it is not safe. */
export const takePostLoginPath = (): string | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  // Once only: a destination that could not be removed is not followed, or every later login would go there too.
  if (!raw || !clearPostLoginPath()) return null;
  try {
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof at !== "number" || Date.now() - at > POST_LOGIN_REDIRECT_TTL_MS || at > Date.now()) return null;

    return isUsable(path) ? path : null;
  } catch {
    return null;
  }
};
